import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BookOpen, Calendar, User, Clock, ChevronRight, ArrowLeft, Tag } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface BlogPostData {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  tags: string[];
  image_url: string;
  published_at: string;
  read_time_minutes: number;
  meta_title?: string | null;
  meta_description?: string | null;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const months = [
    'jaanuar', 'veebruar', 'märts', 'aprill', 'mai', 'juuni',
    'juuli', 'august', 'september', 'oktoober', 'november', 'detsember'
  ];
  return `${d.getDate()}. ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);

    supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .single()
      .then(({ data, error }) => {
        if (error || !data) {
          setNotFound(true);
        } else {
          setPost(data as BlogPostData);
        }
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="container mx-auto py-24 px-4 flex justify-center">
        <div className="animate-pulse space-y-6 w-full max-w-3xl">
          <div className="h-8 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/blogi" replace />;
  }

  if (!post) return null;

  const canonicalUrl = `https://xn--tuks-0qa.ee/blogi/${post.slug}`;
  const ogImage = post.image_url || 'https://xn--tuks-0qa.ee/Touks-icon4.webp';
  const seoTitle = post.meta_title || `${post.title} | Tõuks.ee Blogi`;
  const seoDescription = post.meta_description || post.excerpt;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={post.tags.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:author" content={post.author} />
        {post.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={ogImage} />

        {/* BlogPosting schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt,
          "author": {
            "@type": "Person",
            "name": post.author
          },
          "publisher": {
            "@type": "Organization",
            "name": "Tõuks.ee",
            "url": "https://xn--tuks-0qa.ee",
            "logo": {
              "@type": "ImageObject",
              "url": "https://res.cloudinary.com/daka2lob2/image/upload/v1751272742/Touks_logo3_ed9w6g.png"
            }
          },
          "datePublished": post.published_at,
          "dateModified": post.published_at,
          "image": ogImage,
          "url": canonicalUrl,
          "keywords": post.tags.join(', '),
          "timeRequired": `PT${post.read_time_minutes}M`,
          "inLanguage": "et"
        })}</script>

        {/* BreadcrumbList */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Kodu", "item": "https://xn--tuks-0qa.ee/" },
            { "@type": "ListItem", "position": 2, "name": "Blogi", "item": "https://xn--tuks-0qa.ee/blogi" },
            { "@type": "ListItem", "position": 3, "name": post.title, "item": canonicalUrl }
          ]
        })}</script>
      </Helmet>

      <div className="container mx-auto py-8 px-4 md:px-6 max-w-4xl">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mt-6 mb-8">
          <Link to="/" className="hover:text-[#4FC1E9] transition-colors">Kodu</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/blogi" className="hover:text-[#4FC1E9] transition-colors">Blogi</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-800 line-clamp-1">{post.title}</span>
        </nav>

        {/* Back link */}
        <Link
          to="/blogi"
          className="inline-flex items-center gap-2 text-[#4FC1E9] font-medium mb-6 hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          Tagasi blogi
        </Link>

        {/* Hero image */}
        {post.image_url && (
          <div className="relative h-64 md:h-96 overflow-hidden rounded-2xl mb-8">
            <img
              src={post.image_url}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Tags */}
        {post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 bg-[#4FC1E9]/10 text-[#4FC1E9] text-sm px-3 py-1 rounded-full"
              >
                <Tag className="w-3 h-3" />
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">{post.title}</h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-8 pb-8 border-b border-gray-200">
          <span className="flex items-center gap-1">
            <User className="w-4 h-4" />
            {post.author}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formatDate(post.published_at)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {post.read_time_minutes} min lugemine
          </span>
        </div>

        {/* Article content — rendered as HTML from Supabase */}
        <article
          className="prose prose-lg max-w-none
            prose-headings:font-bold prose-headings:text-gray-900
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
            prose-a:text-[#4FC1E9] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-gray-900
            prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
            prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4
            prose-li:mb-1 prose-li:text-gray-700
            prose-table:w-full prose-table:border-collapse
            prose-th:bg-gray-50 prose-th:p-3 prose-th:text-left prose-th:border prose-th:border-gray-200 prose-th:font-semibold
            prose-td:p-3 prose-td:border prose-td:border-gray-200
            prose-img:rounded-xl prose-img:shadow-md
            prose-blockquote:border-l-4 prose-blockquote:border-[#4FC1E9] prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Footer CTA */}
        <div className="mt-12 p-6 bg-gradient-to-r from-[#4FC1E9]/10 to-blue-50 rounded-2xl">
          <div className="flex items-center gap-2 text-[#4FC1E9] mb-2">
            <BookOpen className="w-5 h-5" />
            <span className="font-semibold">Rohkem lugemist</span>
          </div>
          <p className="text-gray-600 mb-4">Avasta veel huvitavaid artikleid tõuksimise maailmast.</p>
          <Link
            to="/blogi"
            className="inline-flex items-center gap-2 bg-[#4FC1E9] text-white px-5 py-2.5 rounded-full font-medium hover:bg-[#3ab4d9] transition-colors"
          >
            Vaata kõiki artikleid
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </>
  );
}
